import React, { useState } from 'react';
import { Form, Input, InputNumber, Tag, Row, Col } from 'antd';
import callApi from '../../Api/config';
import { InvertFillButton } from '../../components/InvertFillButton';
import { getAdminName } from '../../service/CookieManager';
import { toastError, toastSuccess } from '../../service/Toast';
import '../../components/style.scss';
import './AgentLedgerPaymentDetails.scss';

type ReviewAction = 'paid' | 'payment_unsuccessful' | 'partial';

const REVIEW_OPTIONS: Array<{
  value: ReviewAction;
  title: string;
  className: string;
}> = [
  {
    value: 'paid',
    title: 'Payment Confirmed - Paid',
    className: 'is-paid',
  },
  {
    value: 'payment_unsuccessful',
    title: 'Payment Not Received',
    className: 'is-unsuccessful',
  },
  {
    value: 'partial',
    title: 'Partial Payment Received',
    className: 'is-partial',
  },
];

const resolveReceiptUrl = (receiptUrl: string) =>
  receiptUrl.startsWith('http')
    ? receiptUrl
    : `https://${process.env.REACT_APP_S3_BUCKET || 'documitra-vault'}.s3.amazonaws.com/${receiptUrl}`;

interface AgentLedgerPaymentDetailsProps {
  payment: any;
  onUpdated?: () => void;
}

const InfoField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Col xs={24} sm={12} lg={6}>
    <div className="info-label">{label}</div>
    <div className="info-value">{value || '—'}</div>
  </Col>
);

const AgentLedgerPaymentDetails: React.FC<AgentLedgerPaymentDetailsProps> = ({
  payment: initialPayment,
  onUpdated,
}) => {
  const [payment, setPayment] = useState(initialPayment);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const cmsUser = getAdminName() || 'CMS User';
  const orderTotal = Number(payment.expectedAmount || payment.amount || 0);
  const alreadyReviewed = Boolean(payment.verifiedAt);
  const [selectedAction, setSelectedAction] = useState<ReviewAction>('paid');

  const paymentMethod = String(payment.provider || payment.paymentMethod || '').toLowerCase();
  const isStripe = paymentMethod.includes('stripe') || Boolean(payment.stripePaymentIntentId);
  const isManualReviewMethod = paymentMethod.includes('zelle') || paymentMethod.includes('venmo');
  /** A/B/C review (Net Received / Account Name) is only for Zelle & Venmo */
  const requiresManualReview = isManualReviewMethod && !isStripe;

  const transactionId =
    payment.stripePaymentIntentId ||
    payment.transactionId ||
    payment.paymentId ||
    payment.id ||
    '—';

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const payload: any = {
        paymentId: payment.id,
        status: selectedAction,
        remarks: values.remarks || '',
        confirmedBy: cmsUser,
        accountName: values.accountName || '',
        netReceived: values.netReceived ?? 0,
        receivedAmount: values.receivedAmount ?? 0,
      };

      const res: any = await callApi('/partner-portal/admin/payments/verify', 'POST', payload);
      if (res?.status || res?.data?.isSuccess === 1) {
        toastSuccess(res?.data?.message || 'Payment review submitted successfully');
        setPayment((prev: any) => ({
          ...prev,
          status: selectedAction,
          accountName: values.accountName,
          confirmedBy: cmsUser,
          netReceived: values.netReceived,
          partialReceived: values.receivedAmount,
          verifiedRemarks: values.remarks,
          verifiedAt: new Date().toISOString(),
        }));
        onUpdated?.();
      } else {
        toastError(res?.data?.message || res?.message || 'Failed to submit payment review');
      }
    } catch (error) {
      console.error('Review error:', error);
      toastError('An error occurred while submitting the review');
    } finally {
      setLoading(false);
    }
  };

  const outcomeLabel = () => {
    const s = (payment.status || '').toLowerCase();
    if (s === 'paid' || s === 'succeeded' || s.includes('payment_done')) {
      return { color: 'success', text: 'Payment Confirmed — Paid' };
    }
    if (s === 'partial') {
      return { color: 'warning', text: 'Partial Payment Received' };
    }
    if (s.includes('unsuccessful') || s.includes('failed')) {
      return { color: 'error', text: 'Payment Unsuccessful' };
    }
    if (s.includes('pending')) {
      return { color: 'warning', text: payment.status || 'Pending Review' };
    }
    return { color: 'default', text: payment.status || 'Reviewed' };
  };

  const selectedMeta = REVIEW_OPTIONS.find(option => option.value === selectedAction);

  const renderConfirmationFields = () => {
    if (selectedAction === 'paid') {
      return (
        <div className="paid-review-fields">
          <Form.Item
            name="netReceived"
            label="Net Received ($)"
            rules={[{ required: true, message: 'Please enter net received amount' }]}
          >
            <InputNumber
              className="content-rounded-field"
              style={{ width: '100%' }}
              precision={2}
              min={0}
              placeholder="Enter net received amount"
            />
          </Form.Item>
          <Form.Item
            name="accountName"
            label="Account Name"
            extra="For internal team use"
            rules={[{ required: true, message: 'Please enter account name' }]}
          >
            <Input className="content-rounded-field" placeholder="Enter account name" />
          </Form.Item>
          <Form.Item label="Confirmed By">
            <Input className="content-rounded-field" value={cmsUser} disabled />
          </Form.Item>
        </div>
      );
    }

    if (selectedAction === 'payment_unsuccessful') {
      return (
        <Form.Item
          name="remarks"
          label="Remark"
          rules={[{ required: true, message: 'Please enter a remark' }]}
        >
          <Input.TextArea
            className="content-rounded-field"
            rows={5}
            placeholder="Enter reason payment was not received"
          />
        </Form.Item>
      );
    }

    return (
      <>
        <Form.Item
          name="receivedAmount"
          label="Received Amount ($)"
          rules={[
            { required: true, message: 'Please enter received amount' },
            {
              validator: (_, value) => {
                const max = orderTotal || Number(payment.amount || 0);
                if (value == null) return Promise.resolve();
                if (Number(value) <= 0) {
                  return Promise.reject(new Error('Amount must be greater than 0'));
                }
                if (max > 0 && Number(value) >= max) {
                  return Promise.reject(
                    new Error('Use Payment Confirmed if the full amount was received')
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            className="content-rounded-field"
            style={{ width: '100%' }}
            precision={2}
            min={0}
            placeholder="Enter amount received"
          />
        </Form.Item>
        <Form.Item
          name="remarks"
          label="Remark"
          rules={[{ required: true, message: 'Please enter a remark' }]}
        >
          <Input.TextArea
            className="content-rounded-field"
            rows={4}
            placeholder="Enter remarks about the partial payment"
          />
        </Form.Item>
      </>
    );
  };

  return (
    <div className="agent-ledger-review" style={{ backgroundColor: 'white' }}>
      <div className="details-content">
        <section className="detail-card payment-summary-card">
          <div className="payment-summary-header">
            <span className="header-title">Review Payment Request</span>
            <Tag
              className={requiresManualReview && !alreadyReviewed ? 'pending-status-badge' : ''}
              color={
                isStripe
                  ? 'success'
                  : requiresManualReview
                    ? alreadyReviewed
                      ? outcomeLabel().color
                      : 'warning'
                    : outcomeLabel().color
              }
            >
              {isStripe
                ? 'Automatic (Stripe)'
                : requiresManualReview
                  ? alreadyReviewed
                    ? outcomeLabel().text
                    : 'Pending Review'
                  : outcomeLabel().text}
            </Tag>
          </div>

          <div className="panel-content">
            <Row gutter={[24, 16]}>
              <InfoField label="Payment ID" value={payment.paymentId || payment.id} />
              <InfoField label="Transaction ID" value={transactionId} />
              <InfoField label="Agent ID" value={payment.agentId || payment.partnerId} />
              <InfoField label="Agent Name" value={payment.partnerName} />
              <InfoField label="Email" value={payment.partnerEmail} />
              <InfoField label="Country" value={payment.countryName} />
              <InfoField
                label="Payment Method"
                value={
                  <span style={{ textTransform: 'capitalize' }}>
                    {payment.provider || payment.paymentMethod || '—'}
                  </span>
                }
              />
              <InfoField label="Orders" value={payment.ordersCount || 0} />
              <InfoField
                label="Paid Amount (User)"
                value={`$${Number(payment.amount || 0).toFixed(2)}`}
              />
              <InfoField label="Expected Amount" value={`$${orderTotal.toFixed(2)}`} />
            </Row>
          </div>
          {(payment.orderCodes || []).length > 0 && (
            <div className="orders-inline">
              <div className="orders-inline-title">Orders:</div>
              <div className="order-tags">
                {(payment.orderCodes || []).map((code: string) => (
                  <Tag key={code}>{code}</Tag>
                ))}
              </div>
            </div>
          )}
        </section>

        {payment.receiptUrl && (
          <section className="detail-card">
            <div className="section-heading">Proof of Payment:</div>
            <div className="receipt-preview">
              <img src={resolveReceiptUrl(payment.receiptUrl)} alt="Payment Receipt" />
            </div>
          </section>
        )}

        {isStripe ? (
          <section className="detail-card">
            <div className="section-heading">Payment Status:</div>
            <div className="panel-content">
              <Row gutter={[24, 16]}>
                <InfoField
                  label="Status"
                  value={
                    <Tag
                      className={outcomeLabel().color === 'warning' ? 'pending-status-badge' : ''}
                      color={outcomeLabel().color}
                    >
                      {outcomeLabel().text}
                    </Tag>
                  }
                />
                <InfoField
                  label="Settlement"
                  value="Automatic via Stripe — no manual confirmation needed"
                />
                <InfoField
                  label="Amount"
                  value={`$${Number(payment.amount || orderTotal || 0).toFixed(2)}`}
                />
              </Row>
            </div>
          </section>
        ) : requiresManualReview && alreadyReviewed ? (
          <section className="detail-card">
            <div className="section-heading">Review Outcome:</div>
            <div className="panel-content">
              <Row gutter={[24, 16]}>
                <InfoField
                  label="Decision"
                  value={
                    <Tag
                      className={outcomeLabel().color === 'warning' ? 'pending-status-badge' : ''}
                      color={outcomeLabel().color}
                    >
                      {outcomeLabel().text}
                    </Tag>
                  }
                />
                {(payment.status === 'paid' || payment.status === 'succeeded') && (
                  <>
                    <InfoField
                      label="Net Received"
                      value={`$${Number(payment.netReceived || 0).toFixed(2)}`}
                    />
                    <InfoField label="Account Name" value={payment.accountName} />
                  </>
                )}
                {payment.status === 'partial' && (
                  <InfoField
                    label="Received Amount"
                    value={`$${Number(payment.partialReceived || payment.netReceived || 0).toFixed(2)}`}
                  />
                )}
                <InfoField label="Confirmed By" value={payment.confirmedBy || payment.verifiedBy} />
                {payment.verifiedRemarks && (
                  <InfoField label="Remark" value={payment.verifiedRemarks} />
                )}
              </Row>
            </div>
          </section>
        ) : requiresManualReview ? (
          <section className="detail-card review-decision-card">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                reviewAction: 'paid',
                netReceived: Number(payment.amount || orderTotal || 0),
              }}
            >
              <Form.Item name="reviewAction" hidden>
                <Input />
              </Form.Item>

              <div className="review-decision-header" style={{ marginTop: '16px' }}>
                <div className="section-heading">Review Decision:</div>
                <div className="review-options">
                  {REVIEW_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={`review-option ${option.className} ${
                        selectedAction === option.value ? 'is-active' : ''
                      }`}
                      onClick={() => {
                        setSelectedAction(option.value);
                        form.setFieldsValue({ reviewAction: option.value });
                      }}
                    >
                      <div className="option-title">{option.title}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="review-split">
                <div className="review-confirm-box">
                  <div className="confirm-title">{selectedMeta?.title}</div>
                  {renderConfirmationFields()}
                  <div className="confirm-actions">
                    <InvertFillButton variant="blue" size="md" type="submit" disabled={loading}>
                      Submit Review
                    </InvertFillButton>
                  </div>
                </div>
              </div>
            </Form>
          </section>
        ) : (
          <section className="detail-card">
            <div className="section-heading">Payment Status:</div>
            <div className="panel-content">
              <Row gutter={[24, 16]}>
                <InfoField
                  label="Status"
                  value={<Tag color={outcomeLabel().color}>{outcomeLabel().text}</Tag>}
                />
                <InfoField
                  label="Amount"
                  value={`$${Number(payment.amount || orderTotal || 0).toFixed(2)}`}
                />
              </Row>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AgentLedgerPaymentDetails;
